import { type ReactNode } from "react";
import { router } from "@inertiajs/react";
import {
  Sprout,
  CalendarDays,
  Target,
  Activity as ActivityIcon,
  PieChart as PieIcon,
  MapPin,
  ExternalLink,
} from "lucide-react";
import { DetailTemplate } from "@/templates/DetailTemplate";
import { ImpactGrid, ImpactTile } from "@/portal/templates/ImpactDashboardTemplate";
import { Card } from "@/components/Card";
import { Menu } from "@/components/Menu";
import { Button } from "@/components/Button";
import { StatCard } from "@/components/StatCard";
import { LineChart } from "@/charts/LineChart";
import { PieChart } from "@/charts/PieChart";

interface ProjectMeta {
  id: string;
  objId: string;
  name: string;
  description: string | null;
  projectUrl: string | null;
  location: { lat: number | string; long: number | string } | null;
  createdAt: string | null;
  lifetimeAllocatedMinor: number;
  lifetimeContributions: number;
}

interface Overview {
  days: number;
  totalAllocatedMinor: number;
  deltaRaisedPct: number;
  sparkRaised: number[];
  totalContributions: number;
  deltaContributionsPct: number;
  sparkContributions: number[];
  contributors: number;
  deltaContributorsPct: number;
  sparkContributors: number[];
  goalsSupported: number;
  labels: string[];
  amountByDay: number[];
  contributionsByDay: number[];
  contributorsByDay: number[];
  allocationByGoal: { name: string; amount: number }[];
  goalsFunded: { name: string; allocatedMinor: number; targetMinor: number }[];
}

interface Props {
  project: ProjectMeta;
  overview: Overview;
}

const RANGES = [
  { id: 7, label: "Last 7 days" },
  { id: 30, label: "Last 30 days" },
  { id: 90, label: "Last 90 days" },
  { id: 365, label: "Last 12 months" },
];

function fmtMoney(minor: number, compact = false) {
  const major = minor / 100;
  if (compact && Math.abs(major) >= 1000)
    return `£${(major / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return `£${major.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function ProjectsShow({ project, overview }: Props) {
  const rangeLabel = RANGES.find((r) => r.id === overview.days)?.label ?? `Last ${overview.days} days`;
  const rangeLower = rangeLabel.toLowerCase();

  function setRange(days: number) {
    router.get(window.location.pathname, { days }, { only: ["overview"], preserveScroll: true, preserveState: true });
  }

  return (
    <DetailTemplate
      title={project.name}
      subtitle={<span className="font-mono text-xs text-fg-subtle">{project.objId}</span>}
      backTo="/projects"
      backLabel="All projects"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Projects", to: "/projects" }, { label: project.name }]}
      leading={
        <span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-5">
          <Sprout />
        </span>
      }
      primaryAction={
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button intent="secondary" size="sm">
              <CalendarDays /> {rangeLabel}
            </Button>
          </Menu.Trigger>
          <Menu.Content align="end">
            <Menu.Label>Time range</Menu.Label>
            {RANGES.map((r) => (
              <Menu.CheckboxItem
                key={r.id}
                checked={overview.days === r.id}
                onCheckedChange={() => setRange(r.id)}
              >
                {r.label}
              </Menu.CheckboxItem>
            ))}
          </Menu.Content>
        </Menu.Root>
      }
    >
      <ImpactGrid>
        <ImpactTile col={3} index={0}>
          <StatCard
            label="Allocated"
            value={fmtMoney(overview.totalAllocatedMinor, true)}
            delta={overview.deltaRaisedPct}
            trend={overview.sparkRaised}
            hint={`vs prior ${rangeLower}`}
          />
        </ImpactTile>
        <ImpactTile col={3} index={1}>
          <StatCard
            label="Contributions"
            value={overview.totalContributions.toLocaleString()}
            delta={overview.deltaContributionsPct}
            trend={overview.sparkContributions}
            hint={rangeLower}
          />
        </ImpactTile>
        <ImpactTile col={3} index={2}>
          <StatCard
            label="Contributors"
            value={overview.contributors.toLocaleString()}
            delta={overview.deltaContributorsPct}
            trend={overview.sparkContributors}
            hint={rangeLower}
          />
        </ImpactTile>
        <ImpactTile col={3} index={3}>
          <StatCard label="Goals" value={overview.goalsSupported.toLocaleString()} hint="in this project" />
        </ImpactTile>

        {/* Contributions over time */}
        <ImpactTile col={8} index={4}>
          <Card className="h-full">
            <Card.Header
              title="Contributions over time"
              description={`Daily contributions, contributors and amount, ${rangeLower}.`}
            />
            <Card.Body>
              {overview.contributionsByDay.every((v) => v === 0) ? (
                <MiniEmpty icon={<ActivityIcon />} text="No contributions in this period." />
              ) : (
                <LineChart
                  labels={overview.labels}
                  series={[
                    { label: "Contributions", data: overview.contributionsByDay, focal: true },
                    { label: "Contributors", data: overview.contributorsByDay },
                    { label: "Amount", data: overview.amountByDay, axis: "right" },
                  ]}
                  formatY={(v) => v.toLocaleString()}
                  formatRightY={(v) => fmtMoney(v, true)}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* Allocation by goal */}
        <ImpactTile col={4} index={5}>
          <Card className="h-full">
            <Card.Header title="Allocation by goal" description="Lifetime split across goals." />
            <Card.Body>
              {overview.allocationByGoal.length === 0 ? (
                <MiniEmpty icon={<PieIcon />} text="No allocations yet." />
              ) : (
                <PieChart
                  data={overview.allocationByGoal.map((g) => ({ label: g.name, value: g.amount }))}
                  centerLabel={{ value: fmtMoney(project.lifetimeAllocatedMinor, true), sub: "allocated" }}
                  height={260}
                />
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* Goals funded */}
        <ImpactTile col={project.description || project.location ? 8 : 12} index={6}>
          <Card className="h-full">
            <Card.Header title="Goals funded" description="Allocation toward each goal in this project." />
            <Card.Body className={overview.goalsFunded.length === 0 ? "" : "p-0"}>
              {overview.goalsFunded.length === 0 ? (
                <MiniEmpty icon={<Target />} text="No goals yet." />
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {overview.goalsFunded.map((g, i) => {
                    const pct =
                      g.targetMinor > 0
                        ? Math.min(100, Math.round((g.allocatedMinor / g.targetMinor) * 100))
                        : 0;
                    return (
                      <li key={i} className="px-4 py-3">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0 truncate text-sm font-medium text-fg">{g.name}</div>
                          {g.targetMinor > 0 && (
                            <div className="shrink-0 tabular text-xs text-fg-muted">{pct}%</div>
                          )}
                        </div>
                        {g.targetMinor > 0 && (
                          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-accent transition-[width] duration-700 ease-out"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        )}
                        <div className="mt-1.5 text-xs text-fg-subtle tabular">
                          {fmtMoney(g.allocatedMinor, true)} allocated
                          {g.targetMinor > 0 && <> of {fmtMoney(g.targetMinor, true)}</>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* About / metadata */}
        {(project.description || project.location || project.projectUrl) && (
          <ImpactTile col={4} index={7}>
            <Card className="h-full">
              <Card.Header title="About" />
              <Card.Body className="flex flex-col gap-3">
                {project.description && (
                  <p
                    className="text-sm leading-relaxed text-fg-muted"
                    style={{ fontFamily: "var(--font-editorial)" }}
                  >
                    {project.description}
                  </p>
                )}
                {project.projectUrl && (
                  <InfoRow label="Project page">
                    <a
                      href={project.projectUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-accent-text hover:underline [&_svg]:size-3"
                    >
                      Visit <ExternalLink />
                    </a>
                  </InfoRow>
                )}
                {project.location && (
                  <InfoRow label="Location">
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${project.location.lat}&mlon=${project.location.long}#map=10/${project.location.lat}/${project.location.long}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-accent-text hover:underline [&_svg]:size-3"
                    >
                      <MapPin /> {String(project.location.lat)}, {String(project.location.long)}
                    </a>
                  </InfoRow>
                )}
              </Card.Body>
            </Card>
          </ImpactTile>
        )}
      </ImpactGrid>

      {/* Lifetime footer */}
      <Card className="mt-3">
        <Card.Body className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-fg-muted">
          {project.createdAt && (
            <span className="tabular">
              Added{" "}
              {new Date(project.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
          <span className="tabular">{project.lifetimeContributions.toLocaleString()} contributions all-time</span>
          <span className="tabular">{fmtMoney(project.lifetimeAllocatedMinor, true)} allocated all-time</span>
        </Card.Body>
      </Card>
    </DetailTemplate>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-xs text-fg-subtle">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-fg">{children}</span>
    </div>
  );
}

function MiniEmpty({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <div className="flex size-9 items-center justify-center rounded-full bg-muted text-fg-muted [&_svg]:size-4">
        {icon}
      </div>
      <p className="text-xs text-fg-muted">{text}</p>
    </div>
  );
}
